import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useLocalization } from '@/shared/hooks/useLocalization';
import { RootState } from '@/shared/redux/reducers';
import { getMenuList } from '@/shared/services/routes/routes.service';
import { SideMenuIconProps } from '@/shared/types/shared-types/side-menu.type';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StoredItem {
  path: string;
  label: string;
  section: string;
}

interface PaletteItem extends StoredItem {
  icon: SideMenuIconProps;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const RECENT_KEY = 'cmd_palette_recent';
const PINNED_KEY = 'cmd_palette_pinned';
const MAX_RECENT = 5;

// ─── Storage helpers ──────────────────────────────────────────────────────────

function readStored(key: string): StoredItem[] {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
}

function writeStored(key: string, value: StoredItem[]): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function toStored(item: PaletteItem): StoredItem {
  return { path: item.path, label: item.label, section: item.section };
}

function enrichItems(stored: StoredItem[], allItems: PaletteItem[]): PaletteItem[] {
  return stored
    .map((s) => allItems.find((a) => a.path === s.path))
    .filter(Boolean) as PaletteItem[];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ label: string; emoji?: string }> = ({ label, emoji }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2.5, pt: 1.5, pb: 0.5 }}>
    {emoji && (
      <Typography component="span" sx={{ fontSize: 11, lineHeight: 1 }}>
        {emoji}
      </Typography>
    )}
    <Box
      sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }}
    />
    <Typography
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1.2px',
        color: 'grey.400'
      }}
    >
      {label}
    </Typography>
  </Box>
);

const KbdHint: React.FC<{ keys: string; label: string }> = ({ keys, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        px: 0.75,
        py: '2px',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '5px',
        fontSize: '10px',
        fontWeight: 600,
        color: 'text.secondary',
        lineHeight: 1.6,
        bgcolor: 'white',
        fontFamily: 'monospace'
      }}
    >
      {keys}
    </Box>
    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const userRole = useSelector((state: RootState) => state.auth.role);
  const userTeam = useSelector((state: RootState) => state.auth.team);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [storedPinned, setStoredPinned] = useState<StoredItem[]>(() => readStored(PINNED_KEY));
  const [storedRecent, setStoredRecent] = useState<StoredItem[]>(() => readStored(RECENT_KEY));
  const inputRef = useRef<HTMLInputElement>(null);

  const allItems = useMemo<PaletteItem[]>(() => {
    const menuList = getMenuList(userRole, userTeam, t);
    const flat: PaletteItem[] = [];
    menuList.forEach((parent) => {
      if (parent.CHILDREN?.length) {
        parent.CHILDREN.forEach((child) => {
          if (child.FULL_PATH) {
            flat.push({
              label: child.DISPLAY_NAME || child.NAME,
              path: child.FULL_PATH,
              section: parent.DISPLAY_NAME || parent.NAME,
              icon: child.ICON
            });
          }
        });
      } else if (parent.FULL_PATH) {
        flat.push({
          label: parent.DISPLAY_NAME || parent.NAME,
          path: parent.FULL_PATH,
          section: parent.DISPLAY_NAME || parent.NAME,
          icon: parent.ICON
        });
      }
    });
    return flat;
  }, [userRole, userTeam, t]);

  const pinnedItems = useMemo(() => enrichItems(storedPinned, allItems), [storedPinned, allItems]);
  const recentItems = useMemo(() => enrichItems(storedRecent, allItems), [storedRecent, allItems]);

  const filteredItems = useMemo<PaletteItem[]>(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter(
      (i) => i.label.toLowerCase().includes(q) || i.section.toLowerCase().includes(q)
    );
  }, [query, allItems]);

  const grouped = useMemo<[string, PaletteItem[]][]>(() => {
    const map = new Map<string, PaletteItem[]>();
    filteredItems.forEach((item) => {
      const arr = map.get(item.section) ?? [];
      arr.push(item);
      map.set(item.section, arr);
    });
    return Array.from(map.entries());
  }, [filteredItems]);

  const navList = useMemo<PaletteItem[]>(() => {
    if (query.trim()) return filteredItems;
    const seen = new Set<string>();
    const result: PaletteItem[] = [];
    for (const item of [...pinnedItems, ...recentItems, ...allItems]) {
      if (!seen.has(item.path)) {
        seen.add(item.path);
        result.push(item);
      }
    }
    return result;
  }, [query, filteredItems, pinnedItems, recentItems, allItems]);

  const isItemPinned = useCallback(
    (path: string) => storedPinned.some((p) => p.path === path),
    [storedPinned]
  );

  const navigateTo = useCallback(
    (item: PaletteItem) => {
      const stored = toStored(item);
      const updated = [stored, ...storedRecent.filter((r) => r.path !== item.path)].slice(
        0,
        MAX_RECENT
      );
      setStoredRecent(updated);
      writeStored(RECENT_KEY, updated);
      navigate(item.path);
      onClose();
    },
    [storedRecent, navigate, onClose]
  );

  const togglePin = useCallback(
    (item: PaletteItem, e: React.MouseEvent) => {
      e.stopPropagation();
      const stored = toStored(item);
      const isPinned = isItemPinned(item.path);
      const updated = isPinned
        ? storedPinned.filter((p) => p.path !== item.path)
        : [stored, ...storedPinned];
      setStoredPinned(updated);
      writeStored(PINNED_KEY, updated);
    },
    [storedPinned, isItemPinned]
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setStoredPinned(readStored(PINNED_KEY));
      setStoredRecent(readStored(RECENT_KEY));
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, navList.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && navList[activeIndex]) {
        navigateTo(navList[activeIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [navList, activeIndex, navigateTo, onClose]
  );

  const renderItem = (item: PaletteItem, showPin = true) => {
    const Icon = item.icon?.logo;
    const pinned = isItemPinned(item.path);
    const active = navList[activeIndex]?.path === item.path;
    return (
      <ListItemButton
        key={item.path}
        onClick={() => navigateTo(item)}
        sx={{
          borderRadius: '8px',
          mx: 1,
          px: 1.5,
          py: 0.875,
          mb: 0.25,
          borderLeft: '3px solid',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          bgcolor: active ? 'rgba(2,116,161,0.07)' : 'transparent',
          '&:hover': { bgcolor: active ? 'rgba(2,116,161,0.07)' : 'grey.50' },
          '&:hover .pin-action': { opacity: 1 },
          gap: 1.5,
          transition: 'background-color 0.1s, border-color 0.1s'
        }}
      >
        {Icon && (
          <Box
            sx={{
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: active ? 'primary.main' : 'text.secondary',
              flexShrink: 0
            }}
          >
            <Icon width={16} height={16} />
          </Box>
        )}
        <Typography
          noWrap
          sx={{
            flex: 1,
            fontSize: 13.5,
            fontWeight: active ? 600 : 400,
            color: active ? 'primary.main' : 'text.primary',
            lineHeight: 1
          }}
        >
          {item.label}
        </Typography>
        {item.section !== item.label && (
          <Chip
            label={item.section}
            size="small"
            sx={{
              height: 17,
              fontSize: '9px',
              bgcolor: 'grey.100',
              color: 'text.secondary',
              borderRadius: '4px',
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        )}
        {showPin && (
          <Tooltip
            title={pinned ? t('COMMAND_PALETTE.UNPIN_TOOLTIP') : t('COMMAND_PALETTE.PIN_TOOLTIP')}
            placement="top"
          >
            <IconButton
              size="small"
              className="pin-action"
              onClick={(e) => togglePin(item, e)}
              sx={{
                opacity: pinned ? 1 : 0,
                width: 22,
                height: 22,
                color: pinned ? 'primary.main' : 'grey.400',
                transition: 'opacity 0.15s'
              }}
            >
              {pinned ? (
                <PushPinIcon sx={{ fontSize: 12 }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: 12 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </ListItemButton>
    );
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          bgcolor: 'rgba(0,16,72,0.4)'
        }}
      />

      {/* Palette */}
      <Box
        role="dialog"
        aria-modal="true"
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed',
          top: '8vh',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1301,
          width: 'calc(100% - 32px)',
          maxWidth: '900px'
        }}
      >
        {/* Paper */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '14px',
            boxShadow: '0 32px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            '&::before': {
              content: '""',
              display: 'block',
              height: '2px',
              background: 'linear-gradient(90deg, #0274A1 0%, #03c3ff 100%)',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10
            }
          }}
        >
          {/* Search bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, height: 62, gap: 1.5 }}>
            <SearchRoundedIcon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
            <InputBase
              inputRef={inputRef}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('COMMAND_PALETTE.PLACEHOLDER')}
              inputProps={{ 'aria-label': 'search pages' }}
              sx={{
                fontSize: 16,
                fontWeight: 400,
                flex: 1,
                '& input': { p: 0, '&::placeholder': { opacity: 0.4 } }
              }}
            />
            <Box
              sx={{
                px: 1,
                py: '3px',
                bgcolor: 'grey.100',
                borderRadius: '6px',
                fontSize: '10.5px',
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: '-0.3px',
                whiteSpace: 'nowrap'
              }}
            >
              ⌘ K
            </Box>
          </Box>

          <Divider sx={{ opacity: 0.6 }} />

          {/* Results */}
          <Box sx={{ maxHeight: 420, overflowY: 'auto', py: 0.5 }}>
            {!query.trim() ? (
              <>
                {pinnedItems.length > 0 && (
                  <Box>
                    <SectionHeader label={t('COMMAND_PALETTE.SECTION_PINNED')} emoji="📌" />
                    <List disablePadding>{pinnedItems.map((item) => renderItem(item))}</List>
                  </Box>
                )}
                {recentItems.length > 0 && (
                  <Box>
                    <SectionHeader label={t('COMMAND_PALETTE.SECTION_RECENT')} emoji="🕐" />
                    <List disablePadding>{recentItems.map((item) => renderItem(item))}</List>
                  </Box>
                )}
                {grouped.map(([section, items]) => (
                  <Box key={section}>
                    <SectionHeader label={section} />
                    <List disablePadding>{items.map((item) => renderItem(item))}</List>
                  </Box>
                ))}
              </>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                  {t('COMMAND_PALETTE.NO_RESULTS')}
                </Typography>
              </Box>
            ) : (
              grouped.map(([section, items]) => (
                <Box key={section}>
                  <SectionHeader label={section} />
                  <List disablePadding>{items.map((item) => renderItem(item))}</List>
                </Box>
              ))
            )}
          </Box>

          {/* Footer */}
          <Divider sx={{ opacity: 0.6 }} />
          <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.25, bgcolor: 'grey.50' }}>
            <KbdHint keys="↑↓" label={t('COMMAND_PALETTE.HINT_NAVIGATE')} />
            <KbdHint keys="↵" label={t('COMMAND_PALETTE.HINT_SELECT')} />
            <KbdHint keys="ESC" label={t('COMMAND_PALETTE.HINT_CLOSE')} />
          </Box>
        </Box>
      </Box>
    </>,
    document.body
  );
};

export default CommandPalette;
